import React, { useState } from "react";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./api";

function fmt(x) {
  try { return new Date(x).toLocaleString(); } catch { return x; }
}

// Manual templates — ALWAYS sent FROM bookings@icefleet.is
export const MANUAL_TEMPLATES = {
  availability: {
    label: "Availability reply",
    subject: () => "Availability – IceFleet",
    body: () =>
      `Hi,\n\nThanks for your message.\n\nPlease confirm:\n- Dates\n- Number of passengers\n- Rental period (weekly/monthly)\n- Pickup location\n\nWe'll check availability and send you an offer.\n\nBest regards,\nIceFleet Bookings`,
  },
  confirmation: {
    label: "Booking confirmation",
    subject: () => "Booking confirmed – IceFleet",
    body: (b) =>
      `Hi,\n\nYour booking is confirmed.\n\nDetails:\n- Vehicle: ${b.vehicle_name || "9-seater van"}\n- Dates: ${fmt(b.start_at)} → ${fmt(b.end_at)}\n- Pickup: ${b.pickup || ""}\n- Dropoff: ${b.dropoff || ""}\n- Price: \n\nLet us know if you need anything else.\n\nBest regards,\nIceFleet Bookings`,
  },
};

// Status-based defaults (keeps pre-existing behaviour for non-template replies)
const STATUS_DEFAULTS = {
  pending: {
    subject: () => "Availability – IceFleet",
    body: (b) => MANUAL_TEMPLATES.availability.body(b),
  },
  confirmed: {
    subject: () => "Booking confirmed – IceFleet",
    body: (b) => MANUAL_TEMPLATES.confirmation.body(b),
  },
  cancelled: {
    subject: () => "Booking cancelled – IceFleet",
    body: () =>
      `Hi,\n\nYour booking has been cancelled. If this was a mistake or you'd like to reschedule, just reply and we'll sort it out.\n\nBest regards,\nIceFleet Bookings`,
  },
  completed: {
    subject: () => "Thank you – IceFleet",
    body: () =>
      `Hi,\n\nThanks for travelling with IceFleet — we hope to see you again. If you have a moment, we'd love your feedback.\n\nBest regards,\nIceFleet Bookings`,
  },
};

export function EmailCustomerDialog({ booking, onClose }) {
  const def = STATUS_DEFAULTS[booking.status] || STATUS_DEFAULTS.pending;
  const [subject, setSubject] = useState(def.subject(booking));
  const [message, setMessage] = useState(def.body(booking));
  const [template, setTemplate] = useState("");
  const [busy, setBusy] = useState(false);

  const applyTemplate = (key) => {
    setTemplate(key);
    if (!key) return;
    const t = MANUAL_TEMPLATES[key];
    if (!t) return;
    setSubject(t.subject(booking));
    setMessage(t.body(booking));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/email`, { subject, message });
      toast.success(`Email sent to ${data.sent_to}`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/70 p-0 sm:p-6" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="email-customer-dialog"
        className="w-full max-w-xl rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-4 max-h-[92vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-sky-700 inline-flex items-center gap-1"><Mail size={12} /> Email customer</p>
            <h3 className="mt-1 font-heading text-xl font-semibold">{booking.customer_name} · {booking.email}</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="email-sender-hint">
              Sending <span className="font-semibold text-slate-700">FROM: bookings@icefleet.is</span> · replies route to bookings@icefleet.is
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="close" className="text-slate-500"><X size={18} /></button>
        </div>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">Template</span>
          <select
            value={template}
            onChange={(e) => applyTemplate(e.target.value)}
            data-testid="email-template-select"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none bg-white"
          >
            <option value="">— None (custom) —</option>
            {Object.entries(MANUAL_TEMPLATES).map(([k, t]) => (
              <option key={k} value={k}>{t.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">Subject</span>
          <input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="email-subject"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">Message</span>
          <textarea
            required
            rows={12}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="email-message"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none font-mono"
          />
        </label>
        <p className="text-xs text-slate-500">
          Booking summary (dates, vehicle, status) is appended automatically.
        </p>
        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy} data-testid="email-send" className="rounded-md bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {busy ? "Sending…" : "Send email"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Download a CSV blob using the auth cookie. */
export async function downloadCSV(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not export CSV");
  }
}
